import { useAuth } from '../context/AuthContext';
import { useLoginModal } from '../components/LoginModalProvider';
import { useState, useRef, useEffect } from "react";
import { supabase } from '../lib/supabase';

/* ─── Google Font: Instrument Serif (display) + DM Sans (body) ─── */
const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet"; l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

/* ─── Design tokens ─── */
const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268", n400: "#8a93a8",
  n200: "#d1d5e0", n150: "#e4e7ef", n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

/* ─── Lucide SVG icons (inline, zero dependency) ─── */
const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.5 }) => {
  const paths = {
    ShieldCheck: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    ShieldAlert: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>,
    Scale: <><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="M7 21h10"/><path d="M12 3v18"/><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/></>,
    MessagesSquare: <><path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v5Z"/><path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"/></>,
    PhoneCall: <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.07 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/><path d="M14.05 2a9 9 0 0 1 8 7.94"/><path d="M14.05 6A5 5 0 0 1 18 10"/></>,
    Mail: <><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>,
    Lock: <><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    Wallet: <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/></>,
    CalendarCheck: <><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="m9 16 2 2 4-4"/></>,
    BadgeCheck: <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></>,
    Search: <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></>,
    X: <><path d="M18 6 6 18"/><path d="m6 6 12 12"/></>,
    ChevronRight: <path d="m9 18 6-6-6-6"/>,
    ArrowRight: <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    Clock: <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    CheckCircle: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>,
    FileQuestion: <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><path d="M10 10.3c.2-.4.5-.8.9-1a2.1 2.1 0 0 1 2.6.4c.3.4.5.8.5 1.3 0 1.3-2 2-2 2"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    Paperclip: <><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ─── Data ─── */
const FAQ_DATA = [
  {
    category: "Payments & Refunds", icon: "Wallet",
    items: [
      { q: "When will I receive my refund?", a: "Refunds are processed within 3–5 business days after a dispute is resolved in your favour. M-Pesa refunds typically arrive within 24 hours once approved." },
      { q: "I was charged but the Pro never showed up.", a: "Go to your Bookings, select the job, and tap 'Report an Issue'. You're protected under the Jirani Guarantee and will receive a full refund after review." },
      { q: "Can I get a partial refund for incomplete work?", a: "Yes. Our team reviews evidence from both parties and may issue a partial refund proportional to what was delivered." },
    ],
  },
  {
    category: "Booking Issues", icon: "CalendarCheck",
    items: [
      { q: "My Pro cancelled last minute. What happens?", a: "Last-minute cancellations by Pros are flagged on their profile. You'll receive a full refund and we'll help you find an available replacement quickly." },
      { q: "How do I cancel or reschedule a booking?", a: "Open Bookings, tap the job, and select 'Reschedule' or 'Cancel'. Cancellations 24+ hours before the scheduled time are free. Late cancellations may incur a small fee." },
      { q: "I booked the wrong service. Can I change it?", a: "Contact your Pro via in-app chat. If they agree, the booking can be updated. If not, cancel and rebook the correct service." },
    ],
  },
  {
    category: "Professional Conduct", icon: "BadgeCheck",
    items: [
      { q: "My Pro was unprofessional or made me uncomfortable.", a: "Your safety is our priority. Report the Pro immediately using the 'Report' button on their profile or booking page. Our Trust & Safety team investigates within 24 hours." },
      { q: "The Pro damaged something in my home.", a: "All verified JiraniPros are covered under our damage protection policy. File a dispute with photos and we'll mediate between you and the Pro." },
      { q: "The Pro didn't do the work as described.", a: "Open a dispute from your Bookings page within 48 hours of job completion. Include photos or a description of what was agreed vs what was delivered." },
    ],
  },
  {
    category: "Account Security", icon: "Lock",
    items: [
      { q: "I think my account was accessed without my permission.", a: "Change your password immediately in Settings › Security. Then contact us below and we'll lock your account while we investigate." },
      { q: "How do I report a fraudulent Pro profile?", a: "Tap the three-dot menu on any Pro's profile and select 'Report Profile'. Our verification team reviews all reports within 48 hours." },
      { q: "My personal information was shared without my consent.", a: "This breaches our Privacy Policy. Contact our Data Protection Officer via the form below. We'll act immediately under Kenya's Data Protection Act." },
    ],
  },
];

const ISSUE_TYPES = [
  "Pro didn't show up", "Poor quality of work", "Unprofessional behaviour",
  "Overcharged / billing issue", "Property damage", "Booking / scheduling problem",
  "Safety concern", "Fraudulent activity", "Other",
];

/* ─── Shared styles ─── */
const inputBase = {
  width: "100%", boxSizing: "border-box",
  padding: "13px 16px",
  border: `1px solid ${T.n200}`,
  borderRadius: 10,
  fontSize: 15,
  outline: "none",
  color: T.n900,
  fontFamily: T.sans,
  background: T.white,
  transition: "border-color .15s, box-shadow .15s",
};

/* ─── Accordion item ─── */
function AccordionItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", textAlign: "left", background: "none", border: "none",
        padding: "22px 0", display: "flex", justifyContent: "space-between",
        alignItems: "center", cursor: "pointer", gap: 20, fontFamily: T.sans,
      }}>
        <span style={{ fontSize: 15, fontWeight: 400, color: T.n800, lineHeight: 1.5, letterSpacing: "-0.01em" }}>{q}</span>
        <span style={{
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
          border: `1px solid ${open ? T.g500 : T.n200}`,
          background: open ? T.g500 : "transparent",
          color: open ? T.white : T.n400,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .22s cubic-bezier(.4,0,.2,1)",
          transform: open ? "rotate(45deg)" : "none",
          fontSize: 18, fontWeight: 300, lineHeight: 1,
        }}>+</span>
      </button>
      <div style={{ overflow: "hidden", maxHeight: open ? 300 : 0, transition: "max-height .32s cubic-bezier(.4,0,.2,1)" }}>
        <p style={{ margin: "0 0 22px", fontSize: 15, color: T.n600, lineHeight: 1.75, paddingRight: 48, fontFamily: T.sans }}>{a}</p>
      </div>
    </div>
  );
}

/* ─── Step indicator dot ─── */
function StepDot({ n, active, done }) {
  return (
    <div style={{
      width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
      background: done || active ? T.g500 : T.n100,
      color: done || active ? T.white : T.n400,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 12, fontWeight: 600, fontFamily: T.sans,
      transition: "all .25s",
      boxShadow: active ? `0 0 0 4px ${T.g100}` : "none",
    }}>
      {done ? <Icon name="CheckCircle" size={14} color={T.white} strokeWidth={2.5}/> : n}
    </div>
  );
}

/* ─── 3-step Dispute Form ─── */
function DisputeForm({ onClose, user }) {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [focused, setFocused] = useState(null);
  const [form, setForm] = useState({ role: "", bookingId: "", issueType: "", description: "", files: [], name: "", phone: "", email: "" });
  const fileRef = useRef();
  const [ref, setRef] = useState(null); // real reference, set only after a successful insert
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const STEPS = ["Your role", "Issue details", "Contact info"];
  const canNext = () => {
    if (step === 0) return form.role !== "";
    if (step === 1) return form.issueType !== "" && form.description.trim().length > 20;
    if (step === 2) return form.name.trim() && form.phone.trim();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .insert({
          user_id: user.id,
          subject: form.issueType,
          description: form.description,
          role: form.role,
          booking_id: form.bookingId || null,
          issue_type: form.issueType,
          file_names: form.files,
          contact_name: form.name,
          contact_phone: form.phone,
          contact_email: form.email || null,
          status: 'open',
          priority: 'normal',
        })
        .select()
        .single();

      if (error) throw error;

      // Real reference derived from the actual inserted row — not fabricated
      setRef(`JRN-${data.id.slice(0, 8).toUpperCase()}`);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting dispute:', err);
      setSubmitError(err.message || "Something went wrong submitting your dispute. Please try again.");
      // Deliberately NOT setting submitted(true) here — a failed
      // submission must never show the success screen.
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return (
    <div style={{ textAlign: "center", padding: "48px 20px 32px", fontFamily: T.sans }}>
      <div style={{
        width: 56, height: 56, borderRadius: "50%", background: T.g50,
        border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center",
        justifyContent: "center", margin: "0 auto 20px",
      }}>
        <Icon name="CheckCircle" size={24} color={T.g600} strokeWidth={1.5}/>
      </div>
      <h3 style={{ fontSize: 20, fontWeight: 600, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.02em" }}>Dispute submitted</h3>
      <p style={{ color: T.n400, fontSize: 14, lineHeight: 1.7, margin: "0 0 16px" }}>Your case reference number</p>
      <div style={{
        display: "inline-block", background: T.n50, color: T.n700,
        fontWeight: 600, fontSize: 16, padding: "10px 24px",
        borderRadius: 8, border: `1px solid ${T.n150}`, marginBottom: 20,
        letterSpacing: "0.08em", fontFamily: "monospace",
      }}>{ref}</div>
      <p style={{ color: T.n400, fontSize: 13, lineHeight: 1.75, marginBottom: 32 }}>
        Our Trust & Safety team will respond within <strong style={{ color: T.n700 }}>24–48 hours</strong> via SMS and email.
      </p>
      <button onClick={onClose} style={{
        background: T.g500, color: T.white, border: "none",
        padding: "13px 40px", borderRadius: 10, fontSize: 14,
        fontWeight: 600, cursor: "pointer", fontFamily: T.sans,
        letterSpacing: "0.01em",
      }}>Done</button>
    </div>
  );

  return (
    <div style={{ fontFamily: T.sans }}>
      {/* Step progress */}
      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 32 }}>
        {STEPS.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : "none" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <StepDot n={i + 1} active={step === i} done={step > i}/>
              <span style={{ fontSize: 10, color: step >= i ? T.g500 : T.n400, fontWeight: step === i ? 600 : 400, whiteSpace: "nowrap", letterSpacing: "0.04em", textTransform: "uppercase" }}>{s}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 1, background: step > i ? T.g400 : T.n200, margin: "0 8px", marginTop: -20, transition: "background .3s" }}/>
            )}
          </div>
        ))}
      </div>

      {/* Step 0 */}
      {step === 0 && (
        <div>
          <p style={{ fontSize: 14, color: T.n600, marginBottom: 16, lineHeight: 1.65 }}>Who are you raising this dispute as?</p>
          {["Client — I hired a Pro", "JiraniPro — I was hired"].map(r => (
            <div key={r} onClick={() => set("role", r)} style={{
              border: `1px solid ${form.role === r ? T.g500 : T.n150}`,
              borderRadius: 10, padding: "14px 16px", marginBottom: 10, cursor: "pointer",
              background: form.role === r ? T.g50 : T.white,
              display: "flex", alignItems: "center", gap: 12,
              transition: "all .18s",
            }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", border: `1.5px solid ${form.role === r ? T.g500 : T.n200}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {form.role === r && <div style={{ width: 9, height: 9, borderRadius: "50%", background: T.g500 }}/>}
              </div>
              <span style={{ fontSize: 14, fontWeight: 500, color: T.n800 }}>{r}</span>
            </div>
          ))}
          <div style={{ marginTop: 20 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: T.n400, display: "block", marginBottom: 7, letterSpacing: "0.06em", textTransform: "uppercase" }}>Booking ID <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
            <input value={form.bookingId} onChange={e => set("bookingId", e.target.value)}
              placeholder="e.g. BK-2024-00391"
              onFocus={() => setFocused("bid")} onBlur={() => setFocused(null)}
              style={{ ...inputBase, borderColor: focused === "bid" ? T.g500 : T.n200, boxShadow: focused === "bid" ? `0 0 0 3px ${T.g100}` : "none" }}
            />
          </div>
        </div>
      )}

      {/* Step 1 */}
      {step === 1 && (
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: T.n400, display: "block", marginBottom: 10, letterSpacing: "0.06em", textTransform: "uppercase" }}>Issue type</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 22 }}>
            {ISSUE_TYPES.map(t => (
              <button key={t} onClick={() => set("issueType", t)} style={{
                padding: "7px 14px", borderRadius: 100,
                border: `1px solid ${form.issueType === t ? T.g500 : T.n200}`,
                background: form.issueType === t ? T.g500 : T.white,
                color: form.issueType === t ? T.white : T.n600,
                fontSize: 13, fontWeight: form.issueType === t ? 600 : 400,
                cursor: "pointer", fontFamily: T.sans, transition: "all .15s",
              }}>{t}</button>
            ))}
          </div>
          <label style={{ fontSize: 11, fontWeight: 600, color: T.n400, display: "block", marginBottom: 7, letterSpacing: "0.06em", textTransform: "uppercase" }}>What happened? <span style={{ color: "#c0392b", textTransform: "none", fontWeight: 400 }}>required</span></label>
          <textarea value={form.description} onChange={e => set("description", e.target.value)}
            onFocus={() => setFocused("desc")} onBlur={() => setFocused(null)}
            placeholder="Describe the issue in detail — dates, times, what was agreed, and what actually happened..."
            rows={5} style={{ ...inputBase, resize: "vertical", lineHeight: 1.65, borderColor: focused === "desc" ? T.g500 : T.n200, boxShadow: focused === "desc" ? `0 0 0 3px ${T.g100}` : "none" }}
          />
          <p style={{ fontSize: 12, color: form.description.trim().length < 20 ? T.n400 : T.g500, marginTop: 6, transition: "color .2s" }}>
            {form.description.trim().length} / 20 minimum characters
          </p>
          <div onClick={() => fileRef.current.click()}
            style={{ border: `1px dashed ${T.n200}`, borderRadius: 10, padding: "18px", textAlign: "center", cursor: "pointer", marginTop: 14, background: T.n50, transition: "all .15s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.g400; e.currentTarget.style.background = T.g50; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.background = T.n50; }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: T.n600 }}>
              <Icon name="Paperclip" size={16} color={T.n400}/>
              <span style={{ fontSize: 13, fontWeight: 500, fontFamily: T.sans }}>Attach photos or documents</span>
            </div>
            <p style={{ fontSize: 11, color: T.n400, margin: "4px 0 0", fontFamily: T.sans }}>JPG, PNG, PDF · max 10 MB each</p>
            <input ref={fileRef} type="file" multiple accept="image/*,.pdf" style={{ display: "none" }}
              onChange={e => set("files", [...form.files, ...Array.from(e.target.files).map(f => f.name)])}/>
          </div>
          {form.files.length > 0 && form.files.map((f, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: T.g50, borderRadius: 8, marginTop: 4, fontSize: 13, fontFamily: T.sans }}>
              <span style={{ color: T.g700 }}>— {f}</span>
              <button onClick={() => set("files", form.files.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: T.n400, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* Step 2 */}
      {step === 2 && (
        <div>
          <p style={{ fontSize: 14, color: T.n600, marginBottom: 20, lineHeight: 1.65 }}>Confirm your contact details so our team can reach you about this case.</p>
          {[
            { label: "Full Name", key: "name", placeholder: "Your full name", required: true },
            { label: "Phone Number", key: "phone", placeholder: "07XX XXX XXX", required: true, type: "tel" },
            { label: "Email Address", key: "email", placeholder: "your@email.com", required: false, type: "email" },
          ].map(({ label, key, placeholder, required, type }) => (
            <div key={key} style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: T.n400, display: "block", marginBottom: 7, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                {label} {required && <span style={{ color: "#c0392b", textTransform: "none", fontWeight: 400 }}>required</span>}
              </label>
              <input type={type || "text"} value={form[key]} onChange={e => set(key, e.target.value)}
                onFocus={() => setFocused(key)} onBlur={() => setFocused(null)}
                placeholder={placeholder}
                style={{ ...inputBase, borderColor: focused === key ? T.g500 : T.n200, boxShadow: focused === key ? `0 0 0 3px ${T.g100}` : "none" }}/>
            </div>
          ))}
          <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 10, padding: "12px 16px", display: "flex", gap: 10, alignItems: "flex-start", marginTop: 4 }}>
            <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>⚠</span>
            <p style={{ fontSize: 12, color: "#92400e", margin: 0, lineHeight: 1.65, fontFamily: T.sans }}>By submitting you confirm this dispute is genuine. False reports may affect your account standing.</p>
          </div>
        </div>
      )}

      {submitError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 16px", marginTop: 20, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>⚠</span>
          <div>
            <p style={{ fontSize: 13, color: "#b91c1c", margin: "0 0 4px", fontFamily: T.sans, fontWeight: 600 }}>Submission failed</p>
            <p style={{ fontSize: 12.5, color: "#b91c1c", margin: 0, fontFamily: T.sans, lineHeight: 1.6 }}>{submitError}</p>
          </div>
        </div>
      )}

      {/* Nav */}
      <div style={{ display: "flex", gap: 10, marginTop: 28 }}></div>

      {/* Nav */}
      <div style={{ display: "flex", gap: 10, marginTop: 28 }}>
        <button onClick={() => step === 0 ? onClose() : setStep(s => s - 1)}
          style={{ flex: 1, padding: "13px", border: `1px solid ${T.n200}`, borderRadius: 10, background: T.white, color: T.n600, fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: T.sans }}>
          {step === 0 ? "Cancel" : "← Back"}
        </button>
        <button onClick={() => step === 2 ? handleSubmit() : setStep(s => s + 1)} disabled={!canNext() || submitting}
          style={{ flex: 2, padding: "13px", border: "none", borderRadius: 10, background: (canNext() && !submitting) ? T.g500 : T.n150, color: (canNext() && !submitting) ? T.white : T.n400, fontSize: 14, fontWeight: 600, cursor: (canNext() && !submitting) ? "pointer" : "not-allowed", fontFamily: T.sans, transition: "all .18s", letterSpacing: "0.01em", opacity: submitting ? 0.7 : 1 }}>
          {step === 2 ? "Submit dispute" : "Continue →"}
        </button>
      </div>
    </div>
  );
}

/* ─── Modal (bottom sheet mobile, centred desktop) ─── */
function DisputeModal({ open, onClose, user }) {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  if (!open) return null;
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(15,17,23,.6)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 9999, animation: "fadeO .2s ease" }}>
      <style>{`
        @keyframes fadeO { from{opacity:0} to{opacity:1} }
        @keyframes slideU { from{transform:translateY(40px);opacity:0} to{transform:translateY(0);opacity:1} }
        @media(min-width:600px){.jrn-sheet{border-radius:16px!important;margin-bottom:40px!important;max-height:90vh!important}}
      `}</style>
      <div className="jrn-sheet" style={{ background: T.white, width: "100%", maxWidth: 520, borderRadius: "20px 20px 0 0", padding: "24px 22px 40px", maxHeight: "94vh", overflowY: "auto", animation: "slideU .26s cubic-bezier(.4,0,.2,1)", boxShadow: "0 -4px 40px rgba(0,0,0,.12)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: T.n900, fontFamily: T.sans, letterSpacing: "-0.02em" }}>Raise a dispute</h2>
            <p style={{ fontSize: 12, color: T.n400, margin: "4px 0 0", fontFamily: T.sans }}>Protected by the Jirani Guarantee</p>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: T.n50, border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", color: T.n600, display: "flex", alignItems: "center", justifyContent: "center", transition: "background .15s" }}
            onMouseEnter={e => e.currentTarget.style.background = T.n100}
            onMouseLeave={e => e.currentTarget.style.background = T.n50}>
            <Icon name="X" size={15} color={T.n600} strokeWidth={2}/>
          </button>
        </div>
        <DisputeForm onClose={onClose} user={user}/>
      </div>
    </div>
  );
}

/* ─── MAIN PAGE ─── */
export default function HelpDisputePage() {
  useFonts();
  const { user } = useAuth();
  const { open: openLogin } = useLoginModal();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const categories = ["All", ...FAQ_DATA.map(f => f.category)];
  const filtered = FAQ_DATA
    .filter(f => activeCategory === "All" || f.category === activeCategory)
    .map(s => ({ ...s, items: s.items.filter(i => search === "" || i.q.toLowerCase().includes(search.toLowerCase()) || i.a.toLowerCase().includes(search.toLowerCase())) }))
    .filter(s => s.items.length > 0);

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
        .jrn-cat-btn { background: none; border: none; cursor: pointer; font-family: ${T.sans}; }
        .jrn-cat-btn:hover { color: ${T.n800} !important; }
        .jrn-support-card:hover { border-color: ${T.n200} !important; background: ${T.n50} !important; }
      `}</style>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(60px,8vw,100px) 20px clamp(52px,7vw,88px)", textAlign: "center", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>

          {/* Trust badge */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 24 }}>
            <Icon name="ShieldCheck" size={14} color={T.g500} strokeWidth={2}/>
            Trust &amp; Safety
          </div>

          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(40px, 7vw, 66px)", fontWeight: 400, margin: "0 0 18px", lineHeight: 1.1, color: T.n900, letterSpacing: "-0.03em" }}>
            How can we<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>help you?</em>
          </h1>

          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, margin: "0 0 36px", lineHeight: 1.65, maxWidth: 440, marginLeft: "auto", marginRight: "auto" }}>
            Find answers, manage disputes, and get support from the Jirani team.
          </p>

          {/* Search bar */}
          <div style={{ position: "relative", maxWidth: 480, margin: "0 auto" }}>
            <span style={{ position: "absolute", left: 18, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
              <Icon name="Search" size={18} color={T.n400} strokeWidth={1.8}/>
            </span>
            <input value={search} onChange={e => setSearch(e.target.value)}
              onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
              placeholder="Search disputes, refunds, bookings, safety…"
              style={{ width: "100%", padding: "16px 48px 16px 50px", border: `1px solid ${searchFocused ? T.g500 : T.n200}`, borderRadius: 12, fontSize: 15, outline: "none", background: T.white, color: T.n900, fontFamily: T.sans, boxShadow: searchFocused ? `0 0 0 4px ${T.g100}` : "0 1px 4px rgba(0,0,0,.06)", transition: "all .18s" }}/>
            {search && (
              <button onClick={() => setSearch("")} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: T.n100, border: "none", borderRadius: "50%", width: 24, height: 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="X" size={12} color={T.n600} strokeWidth={2.5}/>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ══ TRUST STRIP ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}`, padding: "0 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
          {[
            { icon: "Clock", label: "Disputes reviewed within 24 hours" },
            { icon: "ShieldCheck", label: "Protected by Jirani Guarantee" },
            { icon: "MessagesSquare", label: "Average support response under 2 hours" },
          ].map(({ icon, label }, i) => (
            <div key={i} style={{ padding: "24px 20px", display: "flex", alignItems: "center", gap: 12, borderRight: i < 2 ? `1px solid ${T.n100}` : "none" }}>
              <Icon name={icon} size={18} color={T.g500} strokeWidth={1.5}/>
              <span style={{ fontSize: 13, color: T.n600, lineHeight: 1.4, fontWeight: 400 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ══ MAIN CONTENT ══ */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "clamp(40px,6vw,72px) 20px clamp(60px,8vw,100px)" }}>

        {/* ── Primary dispute module ── */}
        <div style={{ border: `1px solid ${T.n150}`, borderRadius: 16, padding: "clamp(24px,4vw,40px)", marginBottom: 48, background: T.n50, display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: T.white, border: `1px solid ${T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="Scale" size={22} color={T.g600} strokeWidth={1.5}/>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <h2 style={{ fontFamily: T.serif, fontSize: "clamp(20px,3.5vw,26px)", fontWeight: 400, margin: "0 0 8px", color: T.n900, letterSpacing: "-0.02em", lineHeight: 1.2 }}>Need help with a completed job?</h2>
              <p style={{ fontSize: 15, color: T.n600, margin: 0, lineHeight: 1.65 }}>You're protected by the Jirani Guarantee. Submit a dispute and our team will review it within 24 hours.</p>
            </div>
          </div>
          <div>
            <button onClick={() => user ? setShowModal(true) : openLogin()} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: T.g500, color: T.white, border: "none", padding: "13px 24px", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em", transition: "opacity .15s" }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
              Start a Dispute
              <Icon name="ArrowRight" size={16} color={T.white} strokeWidth={2}/>
            </button>
          </div>
        </div>

        {/* ── Support options ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 72 }}>
          {[
            { icon: "MessagesSquare", label: "Live Chat", sub: "Chat on WhatsApp now", href: "https://wa.me/254700000000" },
            { icon: "PhoneCall",      label: "Call Support",  sub: "0800 720 000 · toll-free", href: "tel:0800720000" },
            { icon: "Mail",           label: "Email Us",      sub: "help@jiraniapp.com", href: "mailto:help@jiraniapp.com" },
          ].map(({ icon, label, sub, href }) => (
            <a key={label} href={href} target="_blank" rel="noreferrer" className="jrn-support-card"
              style={{ border: `1px solid ${T.n150}`, borderRadius: 12, padding: "18px 16px", cursor: "pointer", background: T.white, textDecoration: "none", display: "flex", flexDirection: "column", gap: 10, transition: "all .18s" }}>
              <Icon name={icon} size={20} color={T.n600} strokeWidth={1.5}/>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.n800, marginBottom: 3, letterSpacing: "-0.01em" }}>{label}</div>
                <div style={{ fontSize: 12, color: T.n400, lineHeight: 1.4 }}>{sub}</div>
              </div>
            </a>
          ))}
        </div>

        {/* ── FAQ ── */}
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.n400, letterSpacing: "0.08em", textTransform: "uppercase", margin: "0 0 12px" }}>Support articles</p>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
            <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, margin: 0, letterSpacing: "-0.03em", color: T.n900 }}>Frequently asked questions</h2>
            {search && <span style={{ fontSize: 13, color: T.n400 }}>{filtered.reduce((n, s) => n + s.items.length, 0)} results</span>}
          </div>
        </div>

        {/* Segmented nav */}
        <div style={{ display: "flex", gap: 0, overflowX: "auto", marginBottom: 40, borderBottom: `1px solid ${T.n100}`, scrollbarWidth: "none" }}>
          {categories.map(c => (
            <button key={c} onClick={() => setActiveCategory(c)} className="jrn-cat-btn"
              style={{ padding: "12px 16px", whiteSpace: "nowrap", fontSize: 13, fontWeight: activeCategory === c ? 600 : 400, color: activeCategory === c ? T.n900 : T.n400, borderBottom: `2px solid ${activeCategory === c ? T.g500 : "transparent"}`, marginBottom: -1, transition: "all .18s", letterSpacing: "-0.01em" }}>
              {c}
            </button>
          ))}
        </div>

        {/* FAQ Items */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: T.n400, fontFamily: T.sans }}>
            <Icon name="FileQuestion" size={40} color={T.n200} strokeWidth={1}/>
            <p style={{ fontSize: 15, fontWeight: 500, color: T.n600, margin: "16px 0 6px" }}>No results for "{search}"</p>
            <p style={{ fontSize: 13, margin: 0 }}>
              Try different keywords or{" "}
              <button onClick={() => user ? setShowModal(true) : openLogin()} style={{ color: T.g500, background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, textDecoration: "underline", fontFamily: T.sans, padding: 0 }}>contact support directly</button>.
            </p>
          </div>
        ) : filtered.map(section => (
          <div key={section.category} style={{ marginBottom: 48 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 4 }}>
              <Icon name={section.icon} size={16} color={T.g500} strokeWidth={1.5}/>
              <h3 style={{ fontSize: 11, fontWeight: 600, color: T.g600, margin: 0, textTransform: "uppercase", letterSpacing: "0.07em" }}>{section.category}</h3>
            </div>
            <div>
              {section.items.map((item, i) => <AccordionItem key={i} {...item}/>)}
            </div>
          </div>
        ))}

        {/* ── Footer ── */}
        <div style={{ borderTop: `1px solid ${T.n100}`, paddingTop: 40, textAlign: "center" }}>
          <p style={{ fontFamily: T.serif, fontSize: "clamp(20px,3vw,26px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.02em" }}>Still need help?</p>
          <p style={{ fontSize: 14, color: T.n400, margin: "0 0 24px", lineHeight: 1.65 }}>
            Monday – Saturday · 8 am – 8 pm EAT<br/>
            <span style={{ color: T.n600, fontWeight: 500 }}>Average response under 2 hours</span>
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => user ? setShowModal(true) : openLogin()} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: T.g500, color: T.white, border: "none", padding: "12px 24px", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em" }}>
              Raise a Dispute
            </button>
            <a href="https://wa.me/254700000000" target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: T.white, color: T.n700, border: `1px solid ${T.n200}`, padding: "12px 24px", borderRadius: 10, fontSize: 14, fontWeight: 600, textDecoration: "none", fontFamily: T.sans, transition: "border-color .15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              <Icon name="MessagesSquare" size={15} color={T.n600} strokeWidth={1.8}/>
              WhatsApp Us
            </a>
          </div>
        </div>
      </div>

      <DisputeModal open={showModal} onClose={() => setShowModal(false)} user={user}/>
    </div>
  );
}